import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";

const fieldCls =
  "w-full border border-white/15 bg-black/40 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]";
const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.3em] text-neutral-400";
const groupCls = "border border-white/10 bg-[#0C0C0C] p-6 md:p-8";

const Field = ({ label, value, onChange, textarea, testid }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea data-testid={testid} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${fieldCls} resize-y`} />
    ) : (
      <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={fieldCls} />
    )}
  </div>
);

export default function SiteContent() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!s) return <div className="py-20 text-center text-sm text-neutral-500">Loading content...</div>;

  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));
  const setArr = (k, i, sub) => (v) =>
    setS((p) => ({ ...p, [k]: p[k].map((item, j) => (j === i ? { ...item, [sub]: v } : item)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Website content updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="site-content-editor">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-medium text-white">Site Content</h1>
          <p className="mt-2 text-sm font-light text-neutral-400">Every word and image on the public website.</p>
        </div>
        <button
          data-testid="content-save-button"
          onClick={save}
          disabled={saving}
          className="bg-[#D4AF37] px-8 py-3 text-xs font-medium uppercase tracking-[0.25em] text-black transition-colors hover:bg-[#F9F1CC] disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save All Changes"}
        </button>
      </div>

      <div className="space-y-8">
        <section className={groupCls}>
          <h2 className="font-display mb-6 text-2xl text-[#D4AF37]">Hero — The Grand Entrance</h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Tagline (script text)" value={s.hero_tagline} onChange={set("hero_tagline")} testid="content-hero-tagline" />
            <Field label="Title Line 1" value={s.hero_line1} onChange={set("hero_line1")} testid="content-hero-line1" />
            <Field label="Title Line 2" value={s.hero_line2} onChange={set("hero_line2")} testid="content-hero-line2" />
            <Field label="Subtitle" value={s.hero_subtitle} onChange={set("hero_subtitle")} testid="content-hero-subtitle" />
            <div className="md:col-span-2">
              <label className={labelCls}>Hero Background Image</label>
              <ImageUpload testid="content-hero-image" value={s.hero_image} onChange={set("hero_image")} />
            </div>
            <div className="md:col-span-2">
              <Field label="Marquee Ribbon Text" value={s.marquee_text} onChange={set("marquee_text")} testid="content-marquee-text" />
            </div>
          </div>
        </section>

        <section className={groupCls}>
          <h2 className="font-display mb-6 text-2xl text-[#D4AF37]">About — Heading & Statistics</h2>
          <Field label="About Heading" value={s.about_heading} onChange={set("about_heading")} testid="content-about-heading" />
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(s.stats || []).map((st, i) => (
              <div key={i} className="border border-white/10 p-4">
                <Field label={`Stat ${i + 1} Value`} value={st.value} onChange={setArr("stats", i, "value")} testid={`content-stat-value-${i}`} />
                <div className="mt-3">
                  <Field label="Label" value={st.label} onChange={setArr("stats", i, "label")} testid={`content-stat-label-${i}`} />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={groupCls}>
          <h2 className="font-display mb-6 text-2xl text-[#D4AF37]">About — The Chapters</h2>
          <div className="space-y-6">
            {(s.chapters || []).map((ch, i) => (
              <div key={i} className="grid gap-5 border border-white/10 p-5 md:grid-cols-2">
                <Field label={`Chapter ${i + 1} Title`} value={ch.title} onChange={setArr("chapters", i, "title")} testid={`content-chapter-title-${i}`} />
                <div>
                  <label className={labelCls}>Chapter Image</label>
                  <ImageUpload testid={`content-chapter-image-${i}`} value={ch.image} onChange={setArr("chapters", i, "image")} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Chapter Text" value={ch.text} onChange={setArr("chapters", i, "text")} textarea testid={`content-chapter-text-${i}`} />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={groupCls}>
          <h2 className="font-display mb-6 text-2xl text-[#D4AF37]">The Founder</h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Name" value={s.founder_name} onChange={set("founder_name")} testid="content-founder-name" />
            <Field label="Title" value={s.founder_title} onChange={set("founder_title")} testid="content-founder-title" />
            <div className="md:col-span-2">
              <label className={labelCls}>Portrait</label>
              <ImageUpload testid="content-founder-image" value={s.founder_image} onChange={set("founder_image")} />
            </div>
            <div className="md:col-span-2">
              <Field label="Biography" value={s.founder_bio} onChange={set("founder_bio")} textarea testid="content-founder-bio" />
            </div>
          </div>
        </section>

        <section className={groupCls}>
          <h2 className="font-display mb-2 text-2xl text-[#D4AF37]">Next Event — Countdown Banner</h2>
          <p className="mb-6 text-xs font-light text-neutral-500">
            Set a date and the countdown banner appears on the homepage automatically. Clear the date to hide it.
          </p>
          <div className="grid gap-5 md:grid-cols-3">
            <Field label="Event Name" value={s.event_name} onChange={set("event_name")} testid="content-event-name" />
            <Field label="Date & Time (e.g. 2026-10-15T18:00)" value={s.event_date} onChange={set("event_date")} testid="content-event-date" />
            <Field label="Venue / City" value={s.event_venue} onChange={set("event_venue")} testid="content-event-venue" />
          </div>
        </section>

        <section className={groupCls}>
          <h2 className="font-display mb-6 text-2xl text-[#D4AF37]">Contact Details</h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Phone 1" value={s.contact_phone1} onChange={set("contact_phone1")} testid="content-phone-1" />
            <Field label="Phone 2" value={s.contact_phone2} onChange={set("contact_phone2")} testid="content-phone-2" />
            <Field label="Email" value={s.contact_email} onChange={set("contact_email")} testid="content-email" />
            <Field label="Address" value={s.contact_address} onChange={set("contact_address")} testid="content-address" />
            <Field label="Facebook URL" value={s.facebook_url} onChange={set("facebook_url")} testid="content-facebook" />
            <Field label="Instagram URL (optional)" value={s.instagram_url} onChange={set("instagram_url")} testid="content-instagram" />
          </div>
        </section>
      </div>
    </div>
  );
}
